/**
 * Algo Engine runtime control + read-only monitor API (Phase 1, admin-only).
 * Mirrors `backend/app/api/v1/algo_execution.py` route-for-route: per-algo
 * execution status / "Emergency Exit All Algo
 * Positions" / runtime status / position-order-signal monitors, plus the
 * global (DB-backed) kill-switch layer.
 */

import api from "@/services/api";
import type {
  AlgoOrder,
  AlgoPosition,
  AlgoSignalLog,
  EmergencyExitRequest,
  EmergencyExitResponse,
  EntriesPausedUpdateRequest,
  ExecutionStatusUpdateRequest,
  GlobalSettingsRead,
  GlobalSettingsUpdateRequest,
  RuntimeStatusResponse,
} from "@/types";

export const algoExecutionService = {
  async setExecutionStatus(algoId: string, payload: ExecutionStatusUpdateRequest): Promise<RuntimeStatusResponse> {
    const { data } = await api.post<RuntimeStatusResponse>(`/algo/${algoId}/execution-status`, payload);
    return data;
  },

  async setEntriesPaused(algoId: string, payload: EntriesPausedUpdateRequest): Promise<RuntimeStatusResponse> {
    const { data } = await api.post<RuntimeStatusResponse>(`/algo/${algoId}/entries-paused`, payload);
    return data;
  },

  async emergencyExitAll(algoId: string, payload: EmergencyExitRequest): Promise<EmergencyExitResponse> {
    const { data } = await api.post<EmergencyExitResponse>(`/algo/${algoId}/emergency-exit`, payload);
    return data;
  },

  async getRuntimeStatus(algoId: string): Promise<RuntimeStatusResponse> {
    const { data } = await api.get<RuntimeStatusResponse>(`/algo/${algoId}/runtime-status`);
    return data;
  },

  async listPositions(algoId: string, params?: { mode?: string; status?: string }): Promise<AlgoPosition[]> {
    const { data } = await api.get<AlgoPosition[]>(`/algo/${algoId}/positions`, { params });
    return data;
  },

  async listOrders(algoId: string, params?: { mode?: string; status?: string }): Promise<AlgoOrder[]> {
    const { data } = await api.get<AlgoOrder[]>(`/algo/${algoId}/orders`, { params });
    return data;
  },

  async listSignals(algoId: string, params?: { run_context?: string }): Promise<AlgoSignalLog[]> {
    const { data } = await api.get<AlgoSignalLog[]>(`/algo/${algoId}/signals`, { params });
    return data;
  },

  async getGlobalSettings(): Promise<GlobalSettingsRead> {
    const { data } = await api.get<GlobalSettingsRead>("/algo/global-settings");
    return data;
  },

  async updateGlobalSettings(payload: GlobalSettingsUpdateRequest): Promise<GlobalSettingsRead> {
    const { data } = await api.post<GlobalSettingsRead>("/algo/global-settings", payload);
    return data;
  },
};

export default algoExecutionService;
